import { useContext, useState, useEffect } from 'react';

import { isEmpty } from 'ramda';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { FlightsContext } from 'components/Pages/Admin/GuestPortal/Flights/context';
import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import { showSnackbar } from 'hooks/useSnackbar';
import {
  getAirlinesThunk,
  getAirportsThunk,
  getScheduledFlightsByFlightNumberThunk,
  getScheduledFlightsByRouteThunk,
} from 'store/itinerariesItem/relocations/thunks';
import { formatRepresentationDate } from 'utils/date';
import { debounce } from 'utils/debounce';

export function useForms(props) {
  const { itemIndex, activeItems, setActiveItems } = props;
  const dispatch = useDispatch();
  const { getCurrentItem, handleItemChange, setScheduledFlights, checkPolicy, dateFormat } = useContext(FlightsContext);
  const currentFlight = getCurrentItem(itemIndex);
  const [airlineOptions, setAirlineOptions] = useState([]);
  const [airportsDepartureOptions, setAirportsDepartureOptions] = useState([]);
  const [airportsArrivalOptions, setAirportsArrivalOptions] = useState([]);
  const [dateValue, setDateValue] = useState(() => {
    if (currentFlight?.departureTime) return currentFlight.departureTime;

    if (!isEmpty(activeItems)) return Object.values(activeItems)[0].departureTime;

    if (props.dateValue) return props.dateValue;

    return `${formatRepresentationDate(new Date())}T00:00:00`;
  });
  const { itemId } = useParams();

  const handleTitleChange = (enteredValue) => {
    setDateValue(enteredValue);
    props.setDateValue?.(enteredValue);

    if (itemIndex !== undefined) {
      setActiveItems({});
      setScheduledFlights(null);
    }
  };

  const getAirlines = async (query, callback) => {
    if (!itemId) return [];
    if (query.length < 2) return;

    dispatch(getAirlinesThunk(itemId, query))
      .then((res) => {
        const formatRes = res?.data?.map((item) => ({ ...item, label: item?.name, value: item?.fsCode })) || [];
        setAirlineOptions(formatRes);
        callback(formatRes);
      })
      .catch(() => showSnackbar({ text: 'Oops…', type: SNACKBAR_SKIN.ERROR }));
  };

  const getAirports = async (query, callback, type) => {
    if (!itemId) return [];
    if (query.length < 2) return;

    dispatch(getAirportsThunk(itemId, query))
      .then((res) => {
        const formatRes =
          res?.data?.map((item) => ({
            ...item,
            label: `${item?.name} (${item?.fsCode})`,
            value: item?.fsCode,
          })) || [];
        if (type === 'departure') setAirportsDepartureOptions(formatRes);
        if (type === 'arrival') setAirportsArrivalOptions(formatRes);
        callback(formatRes);
      })
      .catch(() => showSnackbar({ text: 'Oops…', type: SNACKBAR_SKIN.ERROR }));
  };

  const getAirlinesDebounced = debounce((query, callback) => getAirlines(query, callback), 1000);
  const getAirportsDebounced = debounce((query, callback, type) => getAirports(query, callback, type), 1000);

  const handleSubmitFlightNumber = async (values) => {
    try {
      const result = await dispatch(getScheduledFlightsByFlightNumberThunk({ itemId, ...values }));
      if (result?.data) return setScheduledFlights(result.data);
    } catch (err) {
      showSnackbar({ text: 'Oops…', type: SNACKBAR_SKIN.ERROR });
    }
  };

  const handleSubmitRoute = async (values) => {
    try {
      const result = await dispatch(getScheduledFlightsByRouteThunk({ itemId, ...values }));
      if (result?.data) return setScheduledFlights(result.data);
    } catch (err) {
      showSnackbar({ text: 'Oops…', type: SNACKBAR_SKIN.ERROR });
    }
  };

  const getFlightNumberInitialValues = (flight) => ({
    departureDate: formatRepresentationDate(dateValue),
    airlineFsCode: flight?.airline?.fsCode || undefined,
    flightNumber: flight?.flightNumber || undefined,
  });

  const getRouteInitialValues = (flight) => ({
    departureDate: formatRepresentationDate(dateValue),
    departureAirportFsCode: flight?.departureAirport?.fsCode || undefined,
    arrivalAirportFsCode: flight?.arrivalAirport?.fsCode || undefined,
  });

  const flightNumberInitialValues = getFlightNumberInitialValues(currentFlight);
  const routeInitialValues = getRouteInitialValues(currentFlight);

  useEffect(() => {
    if (currentFlight) {
      setAirportsDepartureOptions([
        {
          ...currentFlight.departureAirport,
          label: `${currentFlight.departureAirport?.name} (${currentFlight.departureAirport?.fsCode})`,
          value: currentFlight.departureAirport?.fsCode,
        },
      ]);
      setAirportsArrivalOptions([
        {
          ...currentFlight.arrivalAirport,
          label: `${currentFlight.arrivalAirport?.name} (${currentFlight.arrivalAirport?.fsCode})`,
          value: currentFlight.arrivalAirport?.fsCode,
        },
      ]);
      setAirlineOptions([
        {
          ...currentFlight.airline,
          label: `${currentFlight.airline?.name}`,
          value: currentFlight.airline?.fsCode,
        },
      ]);
    }
    return () => {
      setScheduledFlights(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    getAirlinesDebounced,
    handleSubmitFlightNumber,
    dateValue,
    airlineOptions,
    handleItemChange,
    handleTitleChange,
    getAirportsDebounced,
    airportsDepartureOptions,
    airportsArrivalOptions,
    handleSubmitRoute,
    flightNumberInitialValues,
    routeInitialValues,
    isClientFacing: !checkPolicy,
    dateFormat,
  };
}
